import type {
  PrinterOrder,
  PrinterOrderItem,
  PrinterOrderItemOption,
  PrinterOrderStatus,
} from '@frontend-libs/pej-cap-printer-plugin';

import type {
  OrderInfoModel,
  OrderInfoStatusModel,
} from '@merchant-clients/order';

const parseTimestamp = (iso: string): number | undefined => {
  const ms = new Date(iso).getTime();
  return ms > 0 ? ms : undefined;
};

export const mapOrderToPrinterOrder = (order: OrderInfoModel): PrinterOrder => {
  return {
    id: Number(order.id),
    items: order.items.map(
      (item): PrinterOrderItem => ({
        item: {
          itemType: undefined,
          metadata: undefined,
          name: item.item?.name ?? item.name,
          posData: undefined,
          recycleDeposits: undefined,
        },
        quantity: item.quantity,
        price: item.price,
        total: item.total,
        options: item.options?.map(
          (option): PrinterOrderItemOption => ({
            name: option.name,
            quantity: option.quantity,
          }),
        ),
      }),
    ),
    total: order.total,
    currency: order.currency,
    customer: {
      fullName: order.guestName,
      phone: order.guestPhone,
      email: order.guestEmail,
    },
    places: order.places?.map((place) => ({ id: place.id, name: place.name })),
    timeCreated: parseTimestamp(order.createdAt),
    timeRequested: order.timeRequested
      ? new Date(order.timeRequested).getTime()
      : undefined,
    orderStatus: mapOrderStatus(order.status),
    fsgmTopup: order.fsgmTopup,
    isVerificationActive: false,
    message: order.message || undefined,
    metadata: undefined,
    offers: undefined,
    paymentOption: order.paymentOption,
    preOrder: !!order.timeRequested,
    qr: undefined,
    receipts: order.receipts
      ? order.receipts.map((receipt) => ({
          receiptNumber: receipt.receiptNumber,
          timestamp: receipt.timestamp,
          amountByTaxRate: undefined,
          controlUnitSerial: undefined,
          netByTaxRate: undefined,
          paymentOption: undefined,
          posId: undefined,
          taxByTaxRate: undefined,
        }))
      : [
          {
            receiptNumber: 0,
            timestamp: parseTimestamp(order.createdAt),
            amountByTaxRate: undefined,
            controlUnitSerial: undefined,
            netByTaxRate: undefined,
            paymentOption: undefined,
            posId: undefined,
            taxByTaxRate: undefined,
          },
        ],
    serviceCharges: undefined,
    tax: order.tax,
    ticketMessage: order.message,
    verification: order.verification,
    useVerifoneReceiptFormat: false,
    tipAmount: undefined,
  };
};

const mapOrderStatus = (_status: OrderInfoStatusModel): PrinterOrderStatus => {
  // Since only merchant tickets are printed ATM we can ignore status since
  // it is not used.
  // There are mismatches between statuses in printer SDK and order API
  // making it tricky to map correctly. Some statuses exist in SDK but not
  // in API and vice versa.
  return 'wait_packaging';
};
